import React, { useState, useEffect, useCallback } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import {
  Plus,
  Search,
  FileText,
  Shield,
  Database,
  Globe,
  Trash2,
  Edit,
  Check,
  X,
  Sparkles,
  FolderOpen,
  Tag,
  Filter,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const SOURCE_TYPE_LABELS = {
  uploaded_file: "Uploaded File",
  internal_document: "Internal Document",
  policy_manual: "Policy Manual",
  spreadsheet: "Spreadsheet",
  url: "URL / Web Resource",
  knowledge_base: "Knowledge Base",
  integrated_system: "Integrated System",
};

const SOURCE_TYPE_ICONS = {
  uploaded_file: FileText,
  internal_document: FileText,
  policy_manual: Shield,
  spreadsheet: Database,
  url: Globe,
  knowledge_base: FolderOpen,
  integrated_system: Database,
};

const SENSITIVITY_COLORS = {
  public: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  internal: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  confidential: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  restricted: "bg-red-500/15 text-red-400 border-red-500/20",
};

const ALL_TAGS = [
  "handbook", "policy", "compliance", "budget", "academic",
  "student_support", "hr", "finance", "procurement",
  "state_requirement", "board_policy",
];

/* ── Source Form (Create/Edit) ── */
function SourceForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: "", source_type: "internal_document", description: "",
    owner: "", department: "", update_frequency: "as_needed",
    sensitivity_level: "internal", access_permissions: "",
    approved_for_ai: false, priority_level: "medium", tags: [], url: "",
    ...(initial || {}),
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const toggleTag = (t) => set("tags", form.tags.includes(t) ? form.tags.filter(x => x !== t) : [...form.tags, t]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <Card className="rounded-xl border-border/60 bg-foreground/[0.02] p-5 space-y-4" data-testid="source-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-xs text-foreground/50 mb-1 block">Source Name *</label>
          <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Employee Handbook" data-testid="source-name-input" />
        </div>
        <div>
          <label className="text-xs text-foreground/50 mb-1 block">Source Type</label>
          <select value={form.source_type} onChange={e => set("source_type", e.target.value)}
            className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm text-foreground">
            {Object.entries(SOURCE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-foreground/50 mb-1 block">Description</label>
          <textarea value={form.description} onChange={e => set("description", e.target.value)}
            rows={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground resize-none"
            placeholder="Brief description of this source..." />
        </div>
        <div>
          <label className="text-xs text-foreground/50 mb-1 block">Owner</label>
          <Input value={form.owner} onChange={e => set("owner", e.target.value)} placeholder="e.g. HR Director" />
        </div>
        <div>
          <label className="text-xs text-foreground/50 mb-1 block">Department</label>
          <Input value={form.department} onChange={e => set("department", e.target.value)} placeholder="e.g. HR, Finance" />
        </div>
        <div>
          <label className="text-xs text-foreground/50 mb-1 block">Sensitivity Level</label>
          <select value={form.sensitivity_level} onChange={e => set("sensitivity_level", e.target.value)}
            className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm text-foreground">
            <option value="public">Public</option>
            <option value="internal">Internal</option>
            <option value="confidential">Confidential</option>
            <option value="restricted">Restricted</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-foreground/50 mb-1 block">Priority</label>
          <select value={form.priority_level} onChange={e => set("priority_level", e.target.value)}
            className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm text-foreground">
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-foreground/50 mb-1 block">Update Frequency</label>
          <select value={form.update_frequency} onChange={e => set("update_frequency", e.target.value)}
            className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm text-foreground">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="as_needed">As Needed</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-foreground/50 mb-1 block">URL (optional)</label>
          <Input value={form.url} onChange={e => set("url", e.target.value)} placeholder="https://..." />
        </div>
      </div>

      {/* AI Approved toggle */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={() => set("approved_for_ai", !form.approved_for_ai)}
          className={`flex h-6 w-11 items-center rounded-full transition-colors ${form.approved_for_ai ? "bg-emerald-500" : "bg-foreground/20"}`}
          data-testid="ai-approved-toggle"
        >
          <span className={`h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${form.approved_for_ai ? "translate-x-6" : "translate-x-1"}`} />
        </button>
        <span className="text-sm text-foreground/70">
          <Sparkles className="h-3.5 w-3.5 inline mr-1 text-[var(--brand)]" />
          Approved for AI Use
        </span>
      </div>

      {/* Tags */}
      <div>
        <label className="text-xs text-foreground/50 mb-2 block flex items-center gap-1">
          <Tag className="h-3 w-3" /> Tags
        </label>
        <div className="flex flex-wrap gap-1.5">
          {ALL_TAGS.map(t => (
            <button key={t} type="button" onClick={() => toggleTag(t)}
              className={`rounded-full px-2.5 py-0.5 text-[11px] border transition-colors ${
                form.tags.includes(t) ? "border-[var(--brand)] bg-[var(--brand)]/10 text-[var(--brand)]" : "border-border/40 text-foreground/40 hover:border-border"
              }`}>
              {t.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        <Button onClick={handleSave} disabled={saving}
          className="h-9 rounded-lg bg-[var(--brand)] text-white text-xs px-4 font-semibold hover:bg-[var(--brand)]/90"
          data-testid="save-source-btn">
          {saving ? "Saving..." : initial ? "Update Source" : "Add Source"}
        </Button>
        <Button variant="outline" onClick={onCancel}
          className="h-9 rounded-lg border-border/60 text-foreground text-xs px-4">
          Cancel
        </Button>
      </div>
    </Card>
  );
}

/* ── Main Source Registry Component ── */
export default function SourceRegistry({ tenantId }) {
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [search, setSearch] = useState("");
  const [filterTag, setFilterTag] = useState("");

  const load = useCallback(async () => {
    const url = `${BACKEND}/api/tenants/${tenantId}/sources${filterTag ? `?tag=${filterTag}` : ""}`;
    const [srcRes, statsRes] = await Promise.all([
      fetch(url), fetch(`${BACKEND}/api/tenants/${tenantId}/sources/stats/summary`),
    ]);
    setSources(await srcRes.json());
    setStats(await statsRes.json());
    setLoading(false);
  }, [tenantId, filterTag]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    const url = editingSource
      ? `${BACKEND}/api/tenants/${tenantId}/sources/${editingSource.id}`
      : `${BACKEND}/api/tenants/${tenantId}/sources`;
    const method = editingSource ? "PATCH" : "POST";
    const res = await fetch(url, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    if (res.ok) {
      toast.success(editingSource ? "Source updated" : "Source registered");
      setShowForm(false);
      setEditingSource(null);
      load();
    } else {
      toast.error("Failed to save source");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this source record?")) return;
    const res = await fetch(`${BACKEND}/api/tenants/${tenantId}/sources/${id}`, { method: "DELETE" });
    if (res.ok) { toast.success("Source removed"); load(); }
    else toast.error("Failed to delete");
  };

  const filtered = sources.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.description?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="text-center py-8 text-foreground/40">Loading source registry...</div>;

  return (
    <div className="space-y-5" data-testid="source-registry">
      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Sources", value: stats.total, icon: Database },
            { label: "Active", value: stats.active, icon: Check },
            { label: "AI Approved", value: stats.ai_approved, icon: Sparkles },
            { label: "Source Types", value: Object.keys(stats.by_type || {}).length, icon: FolderOpen },
          ].map(m => (
            <Card key={m.label} className="rounded-lg border-border/60 bg-foreground/[0.02] px-4 py-3">
              <div className="flex items-center gap-1.5 text-xs text-foreground/40 mb-1">
                <m.icon className="h-3.5 w-3.5" /> {m.label}
              </div>
              <div className="text-base font-semibold text-foreground">{m.value}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground/30" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9" placeholder="Search sources..." data-testid="source-search" />
          </div>
          <select value={filterTag} onChange={e => setFilterTag(e.target.value)}
            className="h-9 rounded-lg border border-border bg-background px-3 text-xs text-foreground">
            <option value="">All tags</option>
            {ALL_TAGS.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <Button onClick={() => { setEditingSource(null); setShowForm(true); }}
          className="h-9 rounded-lg bg-[var(--brand)] text-white text-xs px-4 font-semibold hover:bg-[var(--brand)]/90"
          data-testid="add-source-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Register Source
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <SourceForm
          initial={editingSource}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingSource(null); }}
        />
      )}

      {/* Source list */}
      {filtered.length === 0 ? (
        <Card className="rounded-xl border-border/60 bg-foreground/[0.02] p-8 text-center">
          <Database className="h-8 w-8 mx-auto text-foreground/20 mb-3" />
          <p className="text-sm text-foreground/40">No sources registered yet.</p>
          <p className="text-xs text-foreground/30 mt-1">Register organizational documents, policies, and data sources to build your Approved Knowledge Base.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(src => {
            const Icon = SOURCE_TYPE_ICONS[src.source_type] || FileText;
            return (
              <Card key={src.id} className="rounded-xl border-border/60 bg-foreground/[0.02] p-4 flex items-start gap-4" data-testid={`source-${src.id}`}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-foreground/[0.04]">
                  <Icon className="h-5 w-5 text-foreground/40" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground">{src.name}</span>
                    <Badge variant="outline" className={`text-[10px] ${SENSITIVITY_COLORS[src.sensitivity_level] || ""}`}>
                      {src.sensitivity_level}
                    </Badge>
                    {src.approved_for_ai && (
                      <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 text-[10px]">
                        <Sparkles className="h-2.5 w-2.5 mr-0.5" /> AI Approved
                      </Badge>
                    )}
                  </div>
                  {src.description && <p className="text-xs text-foreground/50 mt-0.5">{src.description}</p>}
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-foreground/30">
                    {src.owner && <span>Owner: {src.owner}</span>}
                    {src.department && <span>Dept: {src.department}</span>}
                    <span>{SOURCE_TYPE_LABELS[src.source_type] || src.source_type}</span>
                  </div>
                  {src.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {src.tags.map(t => (
                        <span key={t} className="rounded-full bg-foreground/[0.04] px-2 py-0.5 text-[10px] text-foreground/40">
                          {t.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => { setEditingSource(src); setShowForm(true); }}
                    className="p-1.5 rounded-lg hover:bg-foreground/[0.05] text-foreground/30 hover:text-foreground/60 transition-colors">
                    <Edit className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => handleDelete(src.id)}
                    className="p-1.5 rounded-lg hover:bg-red-500/10 text-foreground/30 hover:text-red-400 transition-colors">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
